/**
 * `fetchActiveContracts` — haalt de actieve huurcontracten op van de
 * ingelogde bezoeker via de Embrace Customers REST API.
 *
 * ## Contract met de Customers-API
 *
 *   - **Endpoint**: `GET {baseUrl}/v1/contract/active/me/paged`
 *   - **Auth**: `Authorization: Bearer <jwt>` — server bepaalt zelf welke
 *     huurder hoort bij dit token (`LoginContext.CurrentPersonIdOrThrow`).
 *     Géén personId/customerId in URL of body nodig.
 *   - **Permissions**: geen extra rol vereist — huurders mogen hun eigen
 *     contracten zien.
 *   - **Query-params** (allemaal optioneel; defaults dekken de happy
 *     path "alleen lopende contracten"):
 *       - `page=0`                        (int, 0-based)
 *       - `pageSize=5`                    (byte, max 255)
 *       - `showFutureContracts=false`     (bool — start in toekomst)
 *       - `showTerminatedContracts=false` (bool — verlopen contracten)
 *
 * ## Multi-contract per huurder
 *
 * Eén persoon kan meerdere actieve contracten hebben (een woning +
 * een parkeerplaats is normaal). `items` is dus geen "1-of-niets" maar
 * een echte lijst. Voor de chat-flow vragen we de bezoeker om de
 * juiste eenheid te kiezen voordat de AVAA-intake start.
 *
 * ## Foutpad-strategie
 *
 * Anders dan `fetchIdentity` (waar "uitgelogd" een normale state is),
 * is een mislukte contract-fetch wél een echte fout — de bezoeker is
 * immers ingelogd, dus we verwachten dat dit werkt. Maar we gooien
 * niet: een gediscrimineerd result-type (`ok: true | false` + `reason`)
 * laat de caller (controller) zelf besluiten of-en-hoe te retry'en
 * zonder een try/catch-rommel.
 */

/**
 * Status-enum zoals geleverd door de API.
 *   1 = Active     — loopt nu
 *   2 = Future     — startDate ligt in de toekomst
 *   3 = Expired    — endDate ligt in het verleden
 *   4 = Cancelled  — geannuleerd vóór ingangsdatum
 */
import { stripTrailingSlash } from '../utils/url';

export type ContractStatus = 1 | 2 | 3 | 4;

/**
 * Eenheid-info uit het contract. De API levert veel velden; voor de
 * chat zijn `id` en een leesbare naam relevant. We laten extra velden
 * via index-access door zodat de UI ze kan gebruiken zonder dat we
 * elke property hier hoeven te typen.
 */
export type ContractUnit = {
  id: string;
  [extra: string]: unknown;
};

/**
 * ContractDetailed — relevante shape voor de chat. We typeren alleen
 * velden die we daadwerkelijk lezen of doorgeven; de rest blijft
 * `unknown` zodat we niet vastlopen op API-uitbreidingen.
 *
 * NB: `accountNumber` is server-side al gemaskeerd (bv.
 * "NL** **** **** 1234"). Niet zelf nog eens maskeren.
 */
export type ContractDetailed = {
  id: string;
  startDate: string | null;
  endDate: string | null;
  status: ContractStatus;
  isActive: boolean;
  isCancelled: boolean;
  isTerminated: boolean;
  hasApproximatedEndDate: boolean;
  accountNumber: string | null;
  grossRent: number | null;
  netRent: number | null;
  grossPrice: number | null;
  subsidizedRent: number | null;
  outstandingBalance: number;
  hasArrears: boolean;
  hasUnpaidBills: boolean;
  hasPaymentArrangement: boolean;
  bailiffCase: boolean;
  unit: ContractUnit | null;
  customers: unknown[] | null;
  coOccupants: unknown[] | null;
  properties: Record<string, unknown> | null;
};

export type PagedContractsResponse = {
  page: number;
  pageSize: number;
  totalCount: number;
  items: ContractDetailed[];
};

export type FetchContractsOptions = {
  /** 0-based pagina-index. Default: server-default (0). */
  page?: number;
  /** Aantal items per pagina. Max 255 (byte). Default: server-default (5). */
  pageSize?: number;
  /** Ook contracten met `startDate` in de toekomst meenemen. Default false. */
  showFutureContracts?: boolean;
  /** Ook verlopen contracten meenemen. Default false. */
  showTerminatedContracts?: boolean;
  /** Time-budget in ms. Default 10_000. */
  timeoutMs?: number;
  /** Override voor tests. Default: globale `fetch`. */
  fetchImpl?: typeof fetch;
  /** Externe abort (controller-disconnect, SPA-route-change). */
  signal?: AbortSignal;
};

/**
 * Discriminated foutpaden zodat de caller gericht kan reageren:
 *
 *   - `network`     — fetch zelf faalde (offline, timeout, DNS-error)
 *   - `unauthorized` — 401/403; JWT verlopen of door server geweigerd
 *   - `unavailable`  — 5xx; tijdelijk server-probleem
 *   - `parse`        — 2xx met onverwachte body-shape
 */
export type ContractsFetchError =
  | 'network'
  | 'unauthorized'
  | 'unavailable'
  | 'parse';

export type ContractsFetchResult =
  | { ok: true; data: PagedContractsResponse }
  | { ok: false; reason: ContractsFetchError };

const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Haalt actieve contracten op. Gooit **nooit** — elke fout wordt gemapt
 * op een `{ ok: false, reason }` zodat de controller met één
 * `if (!result.ok)`-tak alle paden afdekt.
 *
 * @param baseUrl  Customers-API base, bv. `https://api.embrace.nl` of
 *                 een same-origin proxy (`/customers`). Trailing slash
 *                 wordt automatisch gestript.
 * @param jwt      JWT van de ingelogde huurder (uit `state.identityJwt`).
 * @param options  Pagination + filtering + test-overrides.
 */
export async function fetchActiveContracts(
  baseUrl: string,
  jwt: string,
  options: FetchContractsOptions = {},
): Promise<ContractsFetchResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;

  // AbortController combineert interne timeout met externe abort.
  // Zelfde pattern als fetchIdentity — `AbortSignal.any` is nog niet
  // overal beschikbaar, een handmatige listener werkt overal.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);

  try {
    const url = buildUrl(baseUrl, options);
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      // 401/403 zijn semantisch identiek voor ons (token werkt niet);
      // 5xx is tijdelijk; alle andere niet-ok responses (404, 4xx) zijn
      // configfouten en behandelen we als unavailable zodat de UI een
      // generieke "kon contracten niet ophalen"-melding toont.
      if (response.status === 401 || response.status === 403) {
        return { ok: false, reason: 'unauthorized' };
      }
      if (response.status >= 500) {
        return { ok: false, reason: 'unavailable' };
      }
      return { ok: false, reason: 'unavailable' };
    }

    const body = (await response.json()) as unknown;
    const parsed = parsePagedResponse(body);
    if (!parsed) return { ok: false, reason: 'parse' };
    return { ok: true, data: parsed };
  } catch (error) {
    // Network/AbortError/JSON-parse-fout → network-pad. Tegenover
    // fetchIdentity (waar fout = "uitgelogd") loggen we hier WEL een
    // warning: voor een ingelogde bezoeker is een falende contract-
    // fetch een echte storing die we willen kunnen traceren.
    if (
      !(error instanceof DOMException && error.name === 'AbortError')
    ) {
      console.warn('[chat] fetchActiveContracts faalde:', error);
    }
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Bouwt de query-string op basis van expliciet gezette options.
 * Defaults worden NIET meegestuurd — server-side defaults zijn de
 * happy-path; redundant params toevoegen vergroot alleen de URL.
 */
function buildUrl(baseUrl: string, options: FetchContractsOptions): string {
  const base = stripTrailingSlash(baseUrl);
  const params = new URLSearchParams();
  if (options.page !== undefined) {
    params.set('page', String(options.page));
  }
  if (options.pageSize !== undefined) {
    params.set('pageSize', String(options.pageSize));
  }
  if (options.showFutureContracts === true) {
    params.set('showFutureContracts', 'true');
  }
  if (options.showTerminatedContracts === true) {
    params.set('showTerminatedContracts', 'true');
  }
  const qs = params.toString();
  return `${base}/v1/contract/active/me/paged${qs ? `?${qs}` : ''}`;
}

/**
 * Defensief parsen — een uitbreiding aan server-zijde mag de chat niet
 * crashen. We valideren alleen de top-level shape (`items` is een array,
 * `page`/`totalCount` zijn nummers); per-item-validatie is `parseContract`.
 *
 * Items die niet door de validatie komen worden gefilterd, niet
 * gegooid — beter een lijst van 4 i.p.v. 5 contracten dan een blanke UI.
 */
function parsePagedResponse(raw: unknown): PagedContractsResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const itemsRaw = obj['items'];
  if (!Array.isArray(itemsRaw)) return null;

  const items: ContractDetailed[] = [];
  for (const entry of itemsRaw) {
    const c = parseContract(entry);
    if (c) items.push(c);
  }

  return {
    page: numberOr(obj['page'], 0),
    pageSize: numberOr(obj['pageSize'], items.length),
    totalCount: numberOr(obj['totalCount'], items.length),
    items,
  };
}

function parseContract(raw: unknown): ContractDetailed | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  // `id` is het enige absoluut-vereiste veld — zonder kunnen we 'm niet
  // selecteren of doorgeven aan de AVAA-intake. Rest mag default-en.
  const id = typeof o['id'] === 'string' ? o['id'] : null;
  if (!id) return null;

  const statusRaw = o['status'];
  const status: ContractStatus =
    statusRaw === 1 || statusRaw === 2 || statusRaw === 3 || statusRaw === 4
      ? statusRaw
      : 1;

  return {
    id,
    startDate: stringOrNull(o['startDate']),
    endDate: stringOrNull(o['endDate']),
    status,
    isActive: Boolean(o['isActive']),
    isCancelled: Boolean(o['isCancelled']),
    isTerminated: Boolean(o['isTerminated']),
    hasApproximatedEndDate: Boolean(o['hasApproximatedEndDate']),
    accountNumber: stringOrNull(o['accountNumber']),
    grossRent: numberOrNull(o['grossRent']),
    netRent: numberOrNull(o['netRent']),
    grossPrice: numberOrNull(o['grossPrice']),
    subsidizedRent: numberOrNull(o['subsidizedRent']),
    outstandingBalance: numberOr(o['outstandingBalance'], 0),
    hasArrears: Boolean(o['hasArrears']),
    hasUnpaidBills: Boolean(o['hasUnpaidBills']),
    hasPaymentArrangement: Boolean(o['hasPaymentArrangement']),
    bailiffCase: Boolean(o['bailiffCase']),
    unit: parseUnit(o['unit']),
    customers: Array.isArray(o['customers']) ? (o['customers'] as unknown[]) : null,
    coOccupants: Array.isArray(o['coOccupants'])
      ? (o['coOccupants'] as unknown[])
      : null,
    properties:
      o['properties'] && typeof o['properties'] === 'object'
        ? (o['properties'] as Record<string, unknown>)
        : null,
  };
}

function parseUnit(raw: unknown): ContractUnit | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o['id'] === 'string' ? o['id'] : null;
  if (!id) return null;
  return { ...o, id };
}

function stringOrNull(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function numberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
